import { useState, useEffect, useCallback } from 'react';
import { useAuth } from './useAuth';

/**
 * Generic data fetching hook that uses the shared API client.
 */
export function useApi<T>(url: string | null) {
  const { api, isAuthenticated } = useAuth();
  const [data, setData] = useState<T | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetch = useCallback(async () => {
    if (!url || !isAuthenticated) return;
    setIsLoading(true);
    setError(null);
    try {
      const result = await api.get<T>(url);
      // Unwrap { success, data } envelope if present
      const unwrapped = result && typeof result === 'object' && 'success' in (result as any) && 'data' in (result as any)
        ? (result as any).data
        : result;
      setData(unwrapped);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load data');
    } finally {
      setIsLoading(false);
    }
  }, [url, isAuthenticated, api]);

  useEffect(() => {
    fetch();
  }, [fetch]);

  return { data, isLoading, error, refetch: fetch };
}
